/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useI18n } from '../contexts/I18nContext';

export default function Stock() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'ADMIN';
  const { showSnackbar } = useSnackbar();
  const { lang } = useI18n();
  const tr = (fr, ar) => (lang === 'ar' ? ar : fr);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [stock, setStock] = useState([]);
  const [form, setForm] = useState({ reference: '', quantity: '' });
  const [loading, setLoading] = useState(true);

  const fetchStock = async () => {
    try {
      setLoading(true);
      const res = await API.get('/stock');
      setStock(res.data);
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || tr('Erreur lors du chargement du stock', 'فشل تحميل المخزون'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchStock();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await API.post('/stock/add', {
        reference: form.reference,
        quantity: Number.parseInt(form.quantity, 10),
      });
      setForm({ reference: '', quantity: '' });
      showSnackbar(tr('Stock mis a jour', 'تم تحديث المخزون'), 'success');
      fetchStock();
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || tr('Erreur lors de la mise a jour du stock', 'فشل تحديث المخزون'),
        'error'
      );
    }
  };

  const totalStock = stock.reduce((acc, s) => acc + (s.quantity || 0), 0);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 6 },
        py: 5,
        background: 'var(--app-bg)',
      }}
    >
      {/* HERO */}
      <Box
        sx={{
          mb: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            fontSize: { xs: 28, sm: 36, md: 42 },
          }}
        >
          {tr('Stock', 'المخزون')}
        </Typography>

        <Typography sx={{ color: 'var(--text-muted)', mt: 1, maxWidth: 500 }}>
          {tr('Gestion des stocks en temps reel.', 'إدارة المخزون في الوقت الفعلي.')}
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          mt={4}
          justifyContent="center"
        >
            <Chip
              label={`${tr('Positions', 'العناصر')}: ${stock.length}`}
              sx={{ bgcolor: '#16a34a', color: 'var(--text-primary)' }}
            />
            <Chip
              label={`${tr('Stock total', 'إجمالي المخزون')}: ${totalStock}`}
              sx={{ bgcolor: '#16a34a', color: 'var(--text-primary)' }}
            />
        </Stack>
      </Box>

      {/* FORM */}
      <Paper
        sx={{
          p: 4,
          mb: 6,
          borderRadius: 4,
          background:
            'var(--surface-2)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 3 }}>
          {tr('Ajouter au stock', 'زيادة المخزون')}
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              name="reference"
              label={tr("Reference de l'article", 'مرجع المنتج')}
              value={form.reference}
              onChange={handleChange}
              required
              fullWidth
              disabled={user?.role !== 'ADMIN'}
              sx={{
                backgroundColor: 'var(--input-bg)',
                borderRadius: 2,
              }}
            >
              {stock.map((row) => (
                <MenuItem key={row.product_id || row.tire_id || row.id} value={row.reference}>
                  {row.reference}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              name="quantity"
              label={tr('Quantite', 'الكمية')}
              type="number"
              value={form.quantity}
              onChange={handleChange}
              required
              fullWidth
              disabled={user?.role !== 'ADMIN'}
              sx={{
                backgroundColor: 'var(--input-bg)',
                borderRadius: 2,
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={user?.role !== 'ADMIN'}
              sx={{
                px: 4,
                borderRadius: 3,
                fontWeight: 600,
                background: 'linear-gradient(90deg, #16a34a, #059669)',
              }}
              >
                {tr('Ajouter', 'إضافة')}
              </Button>
          </Stack>
        </Box>
      </Paper>

      {/* LIST / TABLE */}
      {loading ? (
        <Stack alignItems="center">
          <CircularProgress sx={{ color: 'var(--text-primary)' }} />
        </Stack>
      ) : isMobile ? (
        <Stack spacing={2}>
          {stock.map((row) => (
            <Paper
              key={row.product_id || row.tire_id || row.id}
              sx={{
                p: 2,
                borderRadius: 4,
                background:
                  'var(--surface-1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <Stack spacing={1}>
                <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                  {row.reference}
                </Typography>
                <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  {row.manufacturer || '-'}
                </Typography>
                <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  {row.supplier_name || '-'}
                </Typography>
                {isAdmin && (
                  <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    {tr('Achat', 'الشراء')}: {row.purchase_price ?? '-'} EUR
                  </Typography>
                )}
                <Chip
                  label={row.quantity}
                  color={row.quantity > 0 ? 'success' : 'error'}
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                />
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Paper
          sx={{
            borderRadius: 4,
            background:
              'var(--surface-1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid var(--border-subtle)',
            overflow: 'hidden',
          }}
        >
          <TableContainer sx={{ maxHeight: { xs: 420, md: 560 } }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    tr('Reference', 'المرجع'),
                    tr('Marque/Fabricant', 'الماركة/المصنع'),
                    tr('Fournisseur', 'المورد'),
                    ...(isAdmin ? [tr('Achat (EUR)', 'الشراء (EUR)')] : []),
                    tr('Quantite', 'الكمية'),
                  ].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        color: 'var(--text-primary)',
                        fontWeight: 700,
                        backgroundColor: 'var(--table-header-bg)',
                        borderBottom: '1px solid var(--border-subtle)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {stock.map((row) => (
                  <TableRow
                    key={row.product_id || row.tire_id || row.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'var(--surface-3)',
                      },
                    }}
                  >
                    <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                      {row.reference}
                    </TableCell>
                    <TableCell sx={{ color: 'var(--text-secondary)' }}>
                      {row.manufacturer || '-'}
                    </TableCell>
                    <TableCell sx={{ color: 'var(--text-secondary)' }}>
                      {row.supplier_name || '-'}
                    </TableCell>
                    {isAdmin && (
                      <TableCell sx={{ color: 'var(--text-secondary)' }}>
                        {row.purchase_price ?? '-'}
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={row.quantity}
                        color={row.quantity > 0 ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}







